import React, { useState, useRef, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ReactArtifactRenderer from '../../components/ReactArtifactRenderer';
import { useChatContext } from '../../contexts/ChatContext';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import './AIChatbot.css';
import { formatAppDate } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin } from '../../utils/roleUtils';
import { chatResponseError } from './aiChatErrors';

// Read-only questions the assistant can answer with its business tools (backend/ai/business_tools.py)
const QUICK_ACTIONS = [
  { key: 'cashFlow', icon: '💵' },
  { key: 'receivables', icon: '🧾' },
  { key: 'sales', icon: '📊' },
  { key: 'inventory', icon: '📦' },
];
// fetch() rejects with the abort reason itself (a string) or with an AbortError
const abortReason = (error) => {
  if (error === 'user_stopped' || error === 'timeout') return error;
  if (error?.name === 'AbortError') return error.reason || 'timeout';
  return null;
};

const SUGGESTED_PROMPTS = ['lateProduction', 'lowStock', 'pendingPurchases', 'openSalesOrders'];

// Memoized message component to prevent re-renders of unchanged messages
const Message = memo(({ message, formatTime }) => {
  const { t } = useTranslation('common');
  return (
  <div
    className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'} ${message.isError ? 'error' : ''}`}
  >
    <div className="message-avatar">
      {message.sender === 'user' ? '👤' : '🤖'}
    </div>
    <div className="message-content">
      {/* Show thinking process if available */}
      {message.thinking && message.thinking.length > 0 && message.sender === 'bot' && (
        <div className="thinking-process">
          <div className="thinking-header">🧠 {t('aiChatbot.reasoningProcess')}:</div>
          <div className="thinking-steps">
            {message.thinking.map((step, idx) => (
              <div key={idx} className="thinking-step">
                <span className="step-number">{idx + 1}.</span> {step}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show tools used if available */}
      {message.tools && message.tools.length > 0 && message.sender === 'bot' && (
        <div className="tools-used">
          <div className="tools-header">🔧 {t('aiChatbot.toolsUsed')}:</div>
          <div className="tools-list-display">
            {message.tools.map((tool, idx) => (
              <div key={idx} className="tool-item">
                <span className="tool-icon">⚙️</span>
                <span className="tool-name">{t(`aiChatbot.tools.${tool.name}`, { defaultValue: tool.name })}</span>
                {tool.input && (
                  <span className="tool-input" title={tool.input}>
                    ({tool.input.substring(0, 50)}...)
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="message-bubble">
        {message.sender === 'bot' ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.textKey ? t(message.textKey) : message.text}</ReactMarkdown>
        ) : (
          message.text
        )}
      </div>
      <div className="message-time">
        {formatTime(message.timestamp)}
      </div>
    </div>
  </div>
  );
});

function AIChatbot() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const canSeeCosts = isAdmin(user); // /api/ai/costs is ADMIN only
  // Use context for persistent state
  const {
    messages,
    setMessages,
    artifacts,
    setArtifacts,
    selectedArtifact,
    setSelectedArtifact,
    showArtifacts,
    setShowArtifacts,
    conversationHistory,
    setConversationHistory,
    conversationId,
    setConversationId,
    scrollPosition,
    setScrollPosition,
    clearChatData
  } = useChatContext();

  // Saved conversations (server-side, per user)
  const [conversations, setConversations] = useState([]);
  const [conversationsError, setConversationsError] = useState(null);

  // Local state for UI-only concerns
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [useStreaming, setUseStreaming] = useState(true); // Toggle for streaming
  const [currentAbortController, setCurrentAbortController] = useState(null); // Track current request to allow stopping
  const [currentTools, setCurrentTools] = useState([]); // Track tools being used
  const [toolHistory, setToolHistory] = useState([]); // Track all tools used in current message
  const [thinkingSteps, setThinkingSteps] = useState([]); // Track AI's thinking process
  const [responseStats, setResponseStats] = useState({ chars: 0, approxTokens: 0 }); // Track response size
  const [showScrollButton, setShowScrollButton] = useState(false); // Show scroll to bottom button
  const [autoScroll, setAutoScroll] = useState(true); // Track if we should auto-scroll
  const [isArtifactsFullscreen, setIsArtifactsFullscreen] = useState(false); // Track artifacts fullscreen state
  const [costData, setCostData] = useState(null); // Track AI usage costs
  const [costError, setCostError] = useState(null);
  const [showCostPanel, setShowCostPanel] = useState(false); // Show/hide cost panel
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);
  const artifactIframeRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false); // Flag to ignore programmatic scrolls

  const scrollToBottom = () => {
    isProgrammaticScrollRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowScrollButton(false);
    // Reset flag after animation completes
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 500);
  };

  // Check if user is at bottom of chat
  const isAtBottom = () => {
    if (!messagesAreaRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = messagesAreaRef.current;
    return scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
  };

  // Track scrolling during streaming
  const prevMessageCount = useRef(messages.length);

  // Handle scroll events to detect user scrolling
  const handleScroll = () => {
    if (!messagesAreaRef.current) return;

    // Ignore programmatic scrolls
    if (isProgrammaticScrollRef.current) return;

    // Check if at bottom
    const atBottom = isAtBottom();

    // Update auto-scroll state based on position
    setAutoScroll(atBottom);

    // Show/hide scroll button
    setShowScrollButton(!atBottom);
  };

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Auto-scroll effect
  useEffect(() => {
    // Skip auto-scroll on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevMessageCount.current = messages.length;
      return;
    }

    // Always scroll for user messages
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.sender === 'user') {
      scrollToBottom();
      setAutoScroll(true);
      return;
    }

    // Auto-scroll if we're supposed to (user is at bottom)
    if (autoScroll) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        isProgrammaticScrollRef.current = true;
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        // Reset flag after animation
        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 500);
      }, 10);
    }

    prevMessageCount.current = messages.length;
  }, [messages, autoScroll]);

  // Restore scroll position when returning to the page
  useEffect(() => {
    const messagesArea = document.querySelector('.messages-area');
    if (!messagesArea) return;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // On mount: restore position or go to bottom
      if (scrollPosition > 0) {
        messagesArea.scrollTop = scrollPosition;
      } else if (messages.length > 0) {
        // Go directly to bottom without any scrolling animation
        messagesArea.scrollTop = messagesArea.scrollHeight;
      }
    });

    // On unmount: save position
    return () => {
      const area = document.querySelector('.messages-area');
      if (area) {
        setScrollPosition(area.scrollTop);
      }
    };
  }, []); // Only on mount/unmount

  // Fetch AI usage costs
  const fetchCosts = async (days = 1) => {
    setCostError(null);
    try {
      const response = await apiFetch(getApiUrl(`/api/ai/costs?workspace=multipack&days=${days}`));
      if (response.ok) {
        const data = await response.json();
        setCostData(data);
        return data;
      }
      throw await chatResponseError(response, t);
    } catch (error) {
      console.error('Error fetching cost data:', error);
      setCostData(null);
      // Without this the panel stayed on "Loading..." forever (#382)
      setCostError(error.userMessage || t('aiChatbot.errors.connection'));
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await apiFetch(API_CONFIG.ENDPOINTS.CHAT_CONVERSATIONS);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setConversations(await response.json());
      setConversationsError(null);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversationsError(t('aiChatbot.errors.loadChats'));
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewChat = () => {
    if (isTyping) return;
    clearChatData();
    setResponseStats({ chars: 0, approxTokens: 0 });
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const loadConversation = async (id) => {
    if (isTyping || id === conversationId) return;
    try {
      const response = await apiFetch(`${API_CONFIG.ENDPOINTS.CHAT_CONVERSATIONS}/${id}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const loaded = data.messages.map((m, idx) => ({
        id: `${id}-${idx}`,
        text: m.text,
        sender: m.role === 'user' ? 'user' : 'bot',
        tools: m.tools || [],
        timestamp: m.created_at ? new Date(m.created_at) : new Date(data.updated_at)
      }));
      setArtifacts([]);
      setSelectedArtifact(null);
      setShowArtifacts(false);
      setConversationHistory(loaded.map(m => ({ sender: m.sender, text: m.text })));
      setMessages(loaded.length ? loaded : [{ id: Date.now(), text: data.title, sender: 'bot', timestamp: new Date() }]);
      setConversationId(data.id);
      setAutoScroll(true);
      setTimeout(() => scrollToBottom(), 50);
    } catch (error) {
      console.error('Error loading conversation:', error);
      setConversationsError(t('aiChatbot.errors.openChat'));
    }
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t('aiChatbot.confirmDeleteChat'))) return;
    try {
      const response = await apiFetch(`${API_CONFIG.ENDPOINTS.CHAT_CONVERSATIONS}/${id}`, { method: 'DELETE' });
      if (!response.ok && response.status !== 404) throw new Error(`HTTP ${response.status}`);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (id === conversationId) startNewChat();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setConversationsError(t('aiChatbot.errors.deleteChat'));
    }
  };

  // Extract code blocks from message text
  const extractArtifacts = (text, messageId) => {
    const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
    const newArtifacts = [];
    let match;

    while ((match = codeBlockRegex.exec(text)) !== null) {
      const language = match[1] || 'text';
      const code = match[2].trim();

      // Only create artifacts for specific languages that can be rendered
      if (['html', 'javascript', 'js', 'jsx', 'react', 'css', 'svg', 'json', 'xml'].includes(language.toLowerCase())) {
        const artifact = {
          id: `${messageId}-${newArtifacts.length}`,
          language: language.toLowerCase(),
          code: code,
          messageId: messageId,
          timestamp: new Date(),
          title: `${language.toUpperCase()} Code`
        };
        newArtifacts.push(artifact);
      }
    }

    return newArtifacts;
  };

  // Render artifact content in iframe or div
  const renderArtifact = (artifact) => {
    if (!artifact) return;

    if (artifact.language === 'html' && artifactIframeRef.current) {
      try {
        // Use srcdoc for HTML content to avoid cross-origin issues
        const iframe = artifactIframeRef.current;
        // Create a data URL for the HTML content
        const htmlContent = artifact.code;
        iframe.srcdoc = htmlContent;
      } catch (error) {
        console.error('Error rendering HTML artifact:', error);
      }
    }
  };

  useEffect(() => {
    if (selectedArtifact && selectedArtifact.language === 'html') {
      // Small delay to ensure iframe is mounted
      setTimeout(() => renderArtifact(selectedArtifact), 100);
    }
  }, [selectedArtifact]);

  // Handle Escape key to exit fullscreen or close artifacts panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showArtifacts) {
        e.preventDefault();
        e.stopPropagation();
        if (isArtifactsFullscreen) {
          // First escape exits fullscreen
          setIsArtifactsFullscreen(false);
        } else {
          // Second escape closes artifacts
          setShowArtifacts(false);
          setIsArtifactsFullscreen(false);
        }
      }
    };

    // Use capture phase to handle event before App.js
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [showArtifacts, isArtifactsFullscreen, setShowArtifacts, setIsArtifactsFullscreen]);

  const handleStreamedResponse = async (currentMessage, convId) => {
    const botMessageId = Date.now() + 1;
    let accumulatedText = '';
    let toolsInProgress = [];
    let allToolsUsed = [];
    let thinkingProcess = [];
    let botMessageAdded = false;

    // Setup AbortController for timeout handling (20 minutes)
    const abortController = new AbortController();
    setCurrentAbortController(abortController);
    const timeoutId = setTimeout(() => {
      abortController.abort('timeout');
      console.warn('Stream timeout - aborting after 20 minutes');
    }, 1200000); // 20 minute timeout

    try {
      const response = await apiFetch(API_CONFIG.ENDPOINTS.CHAT_STREAM, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: currentMessage,
          conversation_id: convId
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw await chatResponseError(response, t);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Add initial empty bot message
      setMessages(prev => [...prev, {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date()
      }]);
      botMessageAdded = true;

      let chunkCount = 0;
      const maxChunks = 10000; // Safety limit to prevent infinite loops

      while (chunkCount < maxChunks) {
        const { done, value } = await reader.read();
        if (done) break;

        chunkCount++;

        // Append new chunk to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            if (data === '[DONE]') {
              setCurrentTools([]);
              // Store tool history in the message
              setMessages(prev => prev.map(msg =>
                msg.id === botMessageId
                  ? { ...msg, tools: allToolsUsed, thinking: thinkingProcess }
                  : msg
              ));
              continue;
            }

            try {
              const parsed = JSON.parse(data);

              if (parsed.type === 'conversation') {
                // The server stores the chat; keep its id to continue it
                setConversationId(parsed.conversation_id);
              } else if (parsed.type === 'token') {
                // Append token to accumulated text and update message
                accumulatedText += parsed.token;
                setMessages(prev => prev.map(msg =>
                  msg.id === botMessageId
                    ? { ...msg, text: accumulatedText }
                    : msg
                ));
              } else if (parsed.type === 'content') {
                // Accumulate content as it streams
                accumulatedText += parsed.content;
                setMessages(prev => prev.map(msg =>
                  msg.id === botMessageId
                    ? { ...msg, text: accumulatedText }
                    : msg
                ));

                // Update response stats less frequently (every 100 chars) to avoid performance issues
                if (accumulatedText.length % 100 === 0 || accumulatedText.length < 100) {
                  setResponseStats({
                    chars: accumulatedText.length,
                    approxTokens: Math.ceil(accumulatedText.length / 4)
                  });
                }

                // Extract artifacts from the accumulated text (with deduplication)
                const extractedArtifacts = extractArtifacts(accumulatedText, botMessageId);
                if (extractedArtifacts.length > 0) {
                  setArtifacts(prev => {
                    // Only add artifacts that don't already exist
                    const existingIds = prev.map(a => a.id);
                    const newArtifacts = extractedArtifacts.filter(a => !existingIds.includes(a.id));
                    if (newArtifacts.length > 0 && prev.length === 0) {
                      setIsArtifactsFullscreen(false);
                      setShowArtifacts(true);
                      setSelectedArtifact(newArtifacts[0]);
                    }
                    return [...prev, ...newArtifacts];
                  });
                }
              } else if (parsed.type === 'tool') {
                // Show tool usage with details
                const toolInfo = {
                  name: parsed.tool,
                  input: parsed.tool_input,
                  timestamp: formatAppDate(new Date(), 'time')
                };
                if (!toolsInProgress.includes(parsed.tool)) {
                  toolsInProgress.push(parsed.tool);
                  allToolsUsed.push(toolInfo);
                  setCurrentTools([...toolsInProgress]);
                  setToolHistory([...allToolsUsed]);
                }
              } else if (parsed.type === 'thinking') {
                // Show AI's thinking process
                thinkingProcess.push(parsed.content);
                setThinkingSteps([...thinkingProcess]);
              } else if (parsed.type === 'step') {
                // Show detailed step information
                const stepInfo = t('aiChatbot.usingTool', { tool: parsed.tool, input: parsed.input?.substring(0, 100) });
                thinkingProcess.push(stepInfo);
                setThinkingSteps([...thinkingProcess]);
              } else if (parsed.type === 'error') {
                console.error('Stream error:', parsed.error);
                // Display error message directly without "Error:" prefix since backend provides formatted messages
                accumulatedText = parsed.error;
                setMessages(prev => prev.map(msg =>
                  msg.id === botMessageId
                    ? { ...msg, text: accumulatedText, isError: true }
                    : msg
                ));
              }
            } catch (e) {
              console.error('Error parsing stream data:', e);
            }
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        // Try to parse remaining buffer as potential data
        try {
          if (buffer.startsWith('data: ')) {
            const data = buffer.slice(6);
            if (data && data !== '[DONE]') {
              const parsed = JSON.parse(data);
              if (parsed.type === 'content' && parsed.content) {
                accumulatedText += parsed.content;
                setMessages(prev => prev.map(msg =>
                  msg.id === botMessageId
                    ? { ...msg, text: accumulatedText }
                    : msg
                ));

                // Update response stats less frequently (every 100 chars) to avoid performance issues
                if (accumulatedText.length % 100 === 0 || accumulatedText.length < 100) {
                  setResponseStats({
                    chars: accumulatedText.length,
                    approxTokens: Math.ceil(accumulatedText.length / 4)
                  });
                }
              }
            }
          }
        } catch (e) {
        }
      }

      // Ensure final text is set
      if (accumulatedText) {
        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, text: accumulatedText, tools: allToolsUsed, thinking: thinkingProcess }
            : msg
        ));

        // Update final response stats
        setResponseStats({
          chars: accumulatedText.length,
          approxTokens: Math.ceil(accumulatedText.length / 4)
        });

        // Update conversation history with bot's response
        setConversationHistory(prev => [...prev, {
          sender: 'bot',
          text: accumulatedText
        }]);

        // Extract artifacts from final text
        const extractedArtifacts = extractArtifacts(accumulatedText, botMessageId);
        if (extractedArtifacts.length > 0) {
          setArtifacts(prev => {
            // Avoid duplicates
            const existingIds = prev.map(a => a.id);
            const newArtifacts = extractedArtifacts.filter(a => !existingIds.includes(a.id));
            return [...prev, ...newArtifacts];
          });
          setIsArtifactsFullscreen(false);
          setShowArtifacts(true);
          setSelectedArtifact(extractedArtifacts[0]);
        }
      } else {
        // If no accumulated text, show error message
        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, text: t('aiChatbot.errors.incomplete'), tools: allToolsUsed }
            : msg
        ));
      }

      // Check if we hit the chunk limit
      if (chunkCount >= maxChunks) {
        console.warn('Reached maximum chunk limit - response may be truncated');
        const warningMsg = '\n\n⚠️ ' + t('aiChatbot.errors.truncated');
        accumulatedText += warningMsg;
        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, text: accumulatedText }
            : msg
        ));
      }
    } catch (error) {
      console.error('Stream error:', error);

      // Failed before the answer started (HTTP error, network): handleSendMessage shows it
      if (!botMessageAdded) throw error;

      // Don't show error message if user manually stopped the generation
      if (abortReason(error)) {
        // Check if this was a user-initiated stop or a timeout
        const isUserStop = abortReason(error) === 'user_stopped';
        if (isUserStop) {
          // User stopped - just update the message with what we have so far
          setMessages(prev => prev.map(msg =>
            msg.id === botMessageId
              ? { ...msg, text: accumulatedText || '⏹️ ' + t('aiChatbot.stoppedByUser') }
              : msg
          ));
        } else {
          // Timeout - show timeout message
          const errorMessage = '⏱️ ' + t('aiChatbot.errors.timeout');
          setMessages(prev => prev.map(msg =>
            msg.id === botMessageId
              ? { ...msg, text: errorMessage }
              : msg
          ));
        }
      } else {
        // Other errors
        let errorMessage = t('aiChatbot.errors.generic', { message: error.message });
        if (error.message?.includes('truncated') || accumulatedText.length > 50000) {
          errorMessage = '📏 ' + t('aiChatbot.errors.tooLarge');
        }
        setMessages(prev => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, text: errorMessage, isError: true }
            : msg
        ));
      }
      // Already shown in the answer bubble; no second error message
    } finally {
      clearTimeout(timeoutId); // Clear the timeout
      setCurrentAbortController(null); // Clear the abort controller
    }
  };

  const sendPlainMessage = async (currentMessage, convId) => {
    const response = await apiFetch(API_CONFIG.ENDPOINTS.CHAT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: currentMessage,
        conversation_id: convId
      })
    });
    if (!response.ok) throw await chatResponseError(response, t);

    const data = await response.json();
    if (data.conversation_id) setConversationId(data.conversation_id);

    const botMessage = {
      id: Date.now() + 1,
      text: data.response,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, botMessage]);

    // Update conversation history with bot response
    setConversationHistory(prev => [...prev, {
      sender: botMessage.sender,
      text: botMessage.text
    }]);

    // Extract and add any artifacts from the bot's response
    const extractedArtifacts = extractArtifacts(data.response, botMessage.id);
    if (extractedArtifacts.length > 0) {
      setArtifacts(prev => [...prev, ...extractedArtifacts]);
      setIsArtifactsFullscreen(false);
      setShowArtifacts(true);
      setSelectedArtifact(extractedArtifacts[0]);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsTyping(true);
    setCurrentTools([]);

    try {
      // Update conversation history with new user message
      const updatedHistory = [...conversationHistory, {
        sender: userMessage.sender,
        text: userMessage.text
      }];
      setConversationHistory(updatedHistory);

      const send = (convId) => (useStreaming
        ? handleStreamedResponse(currentMessage, convId)
        : sendPlainMessage(currentMessage, convId));
      try {
        await send(conversationId);
      } catch (error) {
        // The saved conversation no longer exists (deleted, or another user's): start a new one (#381)
        if (!error.conversationGone || !conversationId) throw error;
        setConversationId(null);
        await send(null);
      }
    } catch (error) {
      console.error('Chat API error:', error);

      // Don't show error message if user manually stopped the generation
      if (abortReason(error) === 'user_stopped') {
        // User stopped - don't add any error message
        return;
      }

      // The real cause (no API key, message too long, no permission...); "connection" only
      // when the server could not be reached (#382)
      const errorMessage = {
        id: Date.now() + 1,
        text: error.userMessage || t('aiChatbot.errors.connection'),
        sender: 'bot',
        isError: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      setCurrentTools([]);
      setToolHistory([]);
      setThinkingSteps([]);
      fetchConversations(); // the list is ordered by last activity
      // Refocus the input after message is sent
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const formatTime = (timestamp) => {
    return formatAppDate(timestamp, 'time');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const clearChat = () => {
    startNewChat();
  };

  const stopGeneration = () => {
    if (currentAbortController) {
      currentAbortController.abort('user_stopped'); // Add reason for abort
      setCurrentAbortController(null);
      setIsTyping(false);
    }
  };

  const setInputAndFocus = (text) => {
    setInputMessage(text);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1 className="page-title">🤖 {t('aiChatbot.title')}</h1>
      </div>
      <div className="ai-container">
        <div className="ai-chatbot">
        <div className="chat-container">
          <div
            className="messages-area"
            ref={messagesAreaRef}
            onScroll={handleScroll}
          >
            {messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                formatTime={formatTime}
              />
            ))}

            <div ref={messagesEndRef} />
          </div>

          {/* Scroll to bottom button */}
          {showScrollButton && (
            <button
              className="scroll-to-bottom-btn"
              onClick={scrollToBottom}
              title={t('aiChatbot.scrollToBottom')}
            >
              ↓
            </button>
          )}

          <form onSubmit={handleSendMessage} className="input-area">
            <div className="input-container">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('aiChatbot.inputPlaceholder')}
                className="message-input"
                rows="1"
                disabled={isTyping}
              />
              <button
                type={isTyping ? "button" : "submit"}
                className="send-button"
                disabled={!isTyping && !inputMessage.trim()}
                onClick={isTyping ? stopGeneration : undefined}
              >
                {isTyping ? '⛔' : '📤'}
              </button>
            </div>
            <div className="action-buttons">
              <button
                onClick={clearChat}
                className="action-button"
                type="button"
                title={t('aiChatbot.newChatHint')}
                disabled={isTyping}
              >
                ➕ {t('aiChatbot.newChat')}
              </button>
              {artifacts.length > 0 && (
                <button
                  onClick={() => setShowArtifacts(!showArtifacts)}
                  className="action-button"
                  type="button"
                >
                  📦 {showArtifacts ? t('aiChatbot.hideArtifacts', { count: artifacts.length }) : t('aiChatbot.showArtifacts', { count: artifacts.length })}
                </button>
              )}
              {canSeeCosts && (
                <button
                  className="action-button"
                  type="button"
                  onClick={() => {
                    if (!showCostPanel) fetchCosts(7); // Fetch last 7 days
                    setShowCostPanel(!showCostPanel);
                  }}
                  title={t('aiChatbot.viewCosts')}
                >
                  💰 {showCostPanel ? t('aiChatbot.hideCosts') : t('aiChatbot.showCosts')}
                </button>
              )}
              {responseStats.approxTokens > 0 && (
                <span className="token-indicator" title={t('aiChatbot.tokensHint')}>
                  📊 {t('aiChatbot.approxTokens', { tokens: responseStats.approxTokens.toLocaleString() })}
                  {responseStats.approxTokens > 12000 && ' ⚠️'}
                </span>
              )}
            </div>
          </form>
        </div>

        <div className="chat-sidebar">
          <div className="sidebar-section conversations-section">
            <div className="conversations-header">
              <h3>{t('aiChatbot.chats')}</h3>
              <button
                type="button"
                className="new-chat-btn"
                onClick={startNewChat}
                disabled={isTyping}
                title={t('aiChatbot.newChat')}
              >
                ➕ {t('ui.new')}
              </button>
            </div>
            {conversationsError && <div className="conversations-error">{conversationsError}</div>}
            {conversations.length === 0 && !conversationsError ? (
              <div className="conversations-empty">{t('aiChatbot.noSavedChats')}</div>
            ) : (
              <ul className="conversation-list">
                {conversations.map(c => (
                  <li
                    key={c.id}
                    className={`conversation-item ${c.id === conversationId ? 'active' : ''}`}
                    onClick={() => loadConversation(c.id)}
                    title={c.title}
                  >
                    <div className="conversation-text">
                      <span className="conversation-title">{c.title}</span>
                      <span className="conversation-date">
                        {formatAppDate(c.updated_at)} {formatAppDate(c.updated_at, 'time')}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="conversation-delete"
                      onClick={(e) => deleteConversation(c.id, e)}
                      title={t('aiChatbot.deleteChat')}
                      disabled={isTyping}
                    >
                      🗑️
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="sidebar-section">
            <h3>{t('aiChatbot.quickActions.title')}</h3>
            <div className="quick-actions">
              {QUICK_ACTIONS.map(({ key, icon }) => (
                <button
                  key={key}
                  className="quick-action-btn"
                  onClick={() => setInputAndFocus(t(`aiChatbot.quickActions.${key}Prompt`))}
                >
                  {icon} {t(`aiChatbot.quickActions.${key}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>{t('aiChatbot.suggestedPrompts.title')}</h3>
            <ul className="prompt-list">
              {SUGGESTED_PROMPTS.map(key => (
                <li key={key} onClick={() => setInputAndFocus(t(`aiChatbot.suggestedPrompts.${key}`))}>
                  {t(`aiChatbot.suggestedPrompts.${key}`)}
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>

      {/* Artifacts Modal */}
      {showArtifacts && artifacts.length > 0 && (
        <div className={`artifacts-modal-overlay ${isArtifactsFullscreen ? 'fullscreen' : ''}`} onClick={() => {
          if (!isArtifactsFullscreen) {
            setShowArtifacts(false);
            setIsArtifactsFullscreen(false);
          }
        }}>
          <div className={`artifacts-modal ${isArtifactsFullscreen ? 'fullscreen' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="artifacts-modal-header">
              <h3>📦 {t('aiChatbot.artifacts')}</h3>
              <div className="artifacts-modal-controls">
                <button
                  className="fullscreen-artifacts"
                  onClick={() => setIsArtifactsFullscreen(!isArtifactsFullscreen)}
                  title={isArtifactsFullscreen ? t('aiChatbot.exitFullscreen') : t('aiChatbot.fullscreen')}
                >
                  {isArtifactsFullscreen ? '⏷' : '⏶'}
                </button>
                <button
                  className="close-artifacts"
                  onClick={() => {
                    setShowArtifacts(false);
                    setIsArtifactsFullscreen(false);
                  }}
                  title={t('ui.close')}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="artifacts-modal-body">
              <div className="artifacts-tabs">
                {artifacts.map((artifact, index) => (
                  <button
                    key={artifact.id}
                    className={`artifact-tab ${selectedArtifact?.id === artifact.id ? 'active' : ''}`}
                    onClick={() => setSelectedArtifact(artifact)}
                  >
                    <span className="artifact-lang">{artifact.language}</span>
                    <span className="artifact-index">#{index + 1}</span>
                  </button>
                ))}
              </div>
              <div className="artifact-content">
                {selectedArtifact && (
                  <>
                    <div className="artifact-toolbar">
                      <span className="artifact-title">{selectedArtifact.title}</span>
                    </div>
                    <div className="artifact-preview">
                      {selectedArtifact.language === 'html' ? (
                        <iframe
                          ref={artifactIframeRef}
                          className="artifact-iframe"
                          title={t('aiChatbot.htmlPreview')}
                          sandbox="allow-scripts"
                          srcdoc={selectedArtifact.code}
                        />
                      ) : ['jsx', 'react'].includes(selectedArtifact.language) ? (
                        <div className="react-artifact-container">
                          <ReactArtifactRenderer code={selectedArtifact.code} />
                        </div>
                      ) : (
                        <pre className="artifact-code">
                          <code className={`language-${selectedArtifact.language}`}>
                            {selectedArtifact.code}
                          </code>
                        </pre>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Panel */}
      {canSeeCosts && showCostPanel && (
        <div className="cost-panel">
          <div className="cost-panel-header">
            <h3>💰 {t('aiChatbot.costs.title')}</h3>
            <button
              className="close-btn"
              onClick={() => setShowCostPanel(false)}
            >
              ✕
            </button>
          </div>
          <div className="cost-panel-content">
            {costError ? (
              <div className="cost-error">
                <p>⚠️ {costError}</p>
              </div>
            ) : costData ? (
              costData.error ? (
                <div className="cost-error">
                  <p>⚠️ {t('aiChatbot.costs.unavailable')}</p>
                  <small>{costData.error}</small>
                </div>
              ) : (
                <div className="cost-details">
                  <div className="cost-summary">
                    <div className="cost-item">
                      <span className="cost-label">{t('aiChatbot.costs.totalCost')}:</span>
                      <span className="cost-value cost-highlight">
                        ${costData.total_cost?.toFixed(4) || '0.0000'}
                      </span>
                    </div>
                    <div className="cost-item">
                      <span className="cost-label">{t('aiChatbot.costs.workspace')}:</span>
                      <span className="cost-value">{costData.workspace || 'multipack'}</span>
                    </div>
                    <div className="cost-item">
                      <span className="cost-label">{t('aiChatbot.costs.totalRequests')}:</span>
                      <span className="cost-value">{costData.total_requests?.toLocaleString() || 0}</span>
                    </div>
                    <div className="cost-item">
                      <span className="cost-label">{t('aiChatbot.costs.inputTokens')}:</span>
                      <span className="cost-value">{costData.total_input_tokens?.toLocaleString() || 0}</span>
                    </div>
                    <div className="cost-item">
                      <span className="cost-label">{t('aiChatbot.costs.outputTokens')}:</span>
                      <span className="cost-value">{costData.total_output_tokens?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                  {costData.by_model && Object.keys(costData.by_model).length > 0 && (
                    <div className="cost-by-model">
                      <h4>{t('aiChatbot.costs.byModel')}:</h4>
                      {Object.entries(costData.by_model).map(([model, data]) => (
                        <div key={model} className="model-cost">
                          <div className="model-name">{model}</div>
                          <div className="model-details">
                            <span>{t('aiChatbot.costs.cost')}: ${data.cost?.toFixed(4) || '0.0000'}</span>
                            <span>{t('aiChatbot.costs.requests')}: {data.requests?.toLocaleString() || 0}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="cost-loading">{t('aiChatbot.costs.loading')}</div>
            )}
          </div>
          <div className="cost-panel-footer">
            <button
              className="refresh-btn"
              onClick={() => fetchCosts(7)}
            >
              🔄 {t('ui.refresh')}
            </button>
            <select
              className="days-selector"
              onChange={(e) => fetchCosts(parseInt(e.target.value))}
              defaultValue="7"
            >
              <option value="1">{t('ui.today')}</option>
              <option value="7">{t('aiChatbot.costs.last7Days')}</option>
              <option value="30">{t('aiChatbot.costs.last30Days')}</option>
            </select>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default AIChatbot;